import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { storage, storageCache, addToStorageCache, getFromStorageCache } from "./firebase";
import { firestore } from "./firebase";
import { collection, doc, addDoc, getDocs, deleteDoc, query, where, orderBy } from "firebase/firestore";
import { secureLog } from "./securityUtils";
import { CheckResult } from "./googleAI";

// Entity Types
export interface Company {
  id: string;
  name: string;
  createdAt: number;
  userId: string;
}

export interface Partner {
  id: string;
  name: string;
  type: 'individual' | 'company' | 'subsidiary' | 'associate' | 'joint_venture';
  registrationNumber: string;
  createdAt: number;
  userId: string;
}

export interface FirmSettings {
  firmName: string;
  registrationNumber: string;
  address?: string;
  contactEmail?: string;
  phone?: string;
  preferences?: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    autoBackup: boolean;
    dataRetentionPeriod: number;
    defaultReportFormat: string;
    theme: string;
  };
  updatedAt: number;
  userId: string;
}

export type DocumentInfo = {
  id: string;
  name: string;
  type: string;
  url: string;
  path: string;
  uploadedAt: number;
  status: string;
  userId: string;
  size: number;
  checksum?: string;
  analysisId?: string;
};

export type AnalysisDocument = {
  type: 'audit_report' | 'annexure_a' | 'annexure_b' | 'balance_sheet' | 'notes' | 'pl_notes' | 'annual_report' | 'secretarial_compliance' | 'cfs';
  file: File;
};

export const uploadFile = async (
  file: File,
  userId: string,
  type: AnalysisDocument["type"]
): Promise<{ url: string; path: string }> => {
  try {
    secureLog('📁 Storage: Starting file upload:', { 
      fileName: file.name, 
      fileSize: file.size, 
      type, 
      userId: '[REDACTED]' 
    });

    // Generate a unique filename
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${type}_${timestamp}.${fileExtension}`;
    const path = `documents/${userId}/${fileName}`;

    // Create storage reference
    const fileRef = storageRef(storage, path);

    // Upload file
    const uploadTask = uploadBytesResumable(fileRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          secureLog('📁 Storage: Upload progress:', { progress: Math.round(progress) });
        },
        (error) => {
          secureLog('❌ Storage: Upload failed:', error);
            reject(error);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            secureLog('✅ Storage: Upload completed:', { 
              path, 
              url: downloadURL.substring(0, 50) + '...' 
            });
            
            // Add to cache
            addToStorageCache(path, downloadURL, file.size);

            resolve({ url: downloadURL, path });
          } catch (error) {
            secureLog('❌ Storage: Error getting download URL:', error);
            reject(error);
          }
        }
      );
    });
  } catch (error) {
    secureLog('❌ Storage: Upload error:', error);
    throw error;
  }
};

export const saveDocumentMetadata = async (
  file: File,
  userId: string,
  type: AnalysisDocument["type"],
  url: string,
  path: string
): Promise<string> => {
  try {
    secureLog('💾 Firestore: Saving document metadata:', { 
      fileName: file.name, 
      type, 
      userId: '[REDACTED]' 
    });

    const documentsCollection = collection(firestore, 'documents');
    const docData = {
      userId,
      name: file.name,
      type,
      url,
      path,
      size: file.size,
      uploadedAt: Date.now(),
      status: "uploaded"
    };

    const docRef = await addDoc(documentsCollection, docData);
    secureLog('✅ Firestore: Document metadata saved:', { documentId: docRef.id });
    
    return docRef.id;
  } catch (error) {
    secureLog('❌ Firestore: Error saving document metadata:', error);
    throw error;
  }
};

export const getUserDocuments = async (userId: string): Promise<DocumentInfo[]> => {
  try {
    secureLog('🔍 Firestore: Getting user documents:', { userId: '[REDACTED]' });

    const documentsCollection = collection(firestore, 'documents');
    const userDocumentsQuery = query(
      documentsCollection,
      where('userId', '==', userId),
      orderBy('uploadedAt', 'desc')
    );
    const snapshot = await getDocs(userDocumentsQuery);

    const documents: DocumentInfo[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      documents.push({
        id: doc.id,
        name: data.name,
        type: data.type,
        url: data.url,
        path: data.path,
        uploadedAt: data.uploadedAt,
        status: data.status,
        userId: data.userId,
        size: data.size,
        checksum: data.checksum,
        analysisId: data.analysisId
      });
    });

    secureLog('✅ Firestore: Retrieved documents:', { count: documents.length });
    return documents;
  } catch (error) {
    secureLog('❌ Firestore: Error getting user documents:', error);
    throw error;
  }
};

export const deleteDocument = async (userId: string, documentId: string): Promise<void> => {
  try {
    secureLog('🗑️ Firestore: Deleting document:', { documentId, userId: '[REDACTED]' });

    const documentDoc = doc(firestore, 'documents', documentId);
    
    // Verify the document belongs to the user
    const snapshot = await getDocs(query(collection(firestore, 'documents'), where('userId', '==', userId)));
    const userDoc = snapshot.docs.find(doc => doc.id === documentId);
    
    if (!userDoc) {
      throw new Error('Document not found or does not belong to user');
    }

    const data = userDoc.data();
    
    // Delete from Firestore
    await deleteDoc(documentDoc);
    
    // Delete from Storage
    try {
      const fileRef = storageRef(storage, data.path);
      await deleteObject(fileRef);
      secureLog('✅ Storage: File deleted from storage:', { path: data.path });
    } catch (storageError) {
      secureLog('⚠️ Storage: Error deleting file from storage:', storageError);
      // Don't throw here as the Firestore deletion succeeded
    }

    secureLog('✅ Firestore: Document deleted successfully:', { documentId });
  } catch (error) {
    secureLog('❌ Firestore: Error deleting document:', error);
    throw error;
  }
};

export const getDocumentsByType = async (userId: string): Promise<{
  auditReports: DocumentInfo[];
  annexureA: DocumentInfo[];
  annexureB: DocumentInfo[];
  balanceSheet: DocumentInfo[];
  notes: DocumentInfo[];
  plNotes: DocumentInfo[];
  annualReports: DocumentInfo[];
  secretarialCompliance: DocumentInfo[];
}> => {
    const allDocs = await getUserDocuments(userId);

    return {
    auditReports: allDocs.filter(doc => doc.type === 'audit_report'),
    annexureA: allDocs.filter(doc => doc.type === 'annexure_a'),
    annexureB: allDocs.filter(doc => doc.type === 'annexure_b'),
    balanceSheet: allDocs.filter(doc => doc.type === 'balance_sheet'),
    notes: allDocs.filter(doc => doc.type === 'notes'),
    plNotes: allDocs.filter(doc => doc.type === 'pl_notes'),
    annualReports: allDocs.filter(doc => doc.type === 'annual_report'),
    secretarialCompliance: allDocs.filter(doc => doc.type === 'secretarial_compliance')
  };
};

export const refreshDocumentUrl = async (userId: string, documentId: string): Promise<string> => {
  try {
    secureLog('🔄 Storage: Refreshing document URL:', { documentId, userId: '[REDACTED]' });

    const documentsCollection = collection(firestore, 'documents');
    const userDocumentsQuery = query(
      documentsCollection,
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(userDocumentsQuery);
    
    const userDoc = snapshot.docs.find(doc => doc.id === documentId);
    if (!userDoc) {
      throw new Error('Document not found');
    }

    const data = userDoc.data();
    const path = data.path;

    // Check cache first
    const cachedUrl = getFromStorageCache(path);
    if (cachedUrl) {
      secureLog('✅ Storage: Using cached URL:', { path });
      return cachedUrl;
    }

    // Get fresh URL from storage
    const fileRef = storageRef(storage, path);
    const newUrl = await getDownloadURL(fileRef);

    // Add to cache
    addToStorageCache(path, newUrl, data.size || 0);
    
    secureLog('✅ Storage: URL refreshed:', { path });
    return newUrl;
  } catch (error) {
    secureLog('❌ Storage: Error refreshing document URL:', error);
    throw error;
  }
};

export const checkFileExists = async (path: string): Promise<{
  exists: boolean;
  url?: string;
  error?: string;
}> => {
  try {
    secureLog('🔍 Storage: Checking if file exists:', { path });

    // Check cache first
    const cachedUrl = getFromStorageCache(path);
    if (cachedUrl) {
      return { exists: true, url: cachedUrl };
    }

    // Check storage
    const fileRef = storageRef(storage, path);
    const url = await getDownloadURL(fileRef);
    
    // Add to cache
    addToStorageCache(path, url, 0);
    
    return { exists: true, url };
  } catch (error: any) {
      if (error.code === 'storage/object-not-found') {
      return { exists: false, error: 'File not found' };
    }
    return { exists: false, error: error.message };
  }
};

export const getDocumentByPath = async (path: string): Promise<DocumentInfo | null> => {
  try {
    secureLog('🔍 Firestore: Getting document by path:', { path });

    const documentsCollection = collection(firestore, 'documents');
    const pathQuery = query(documentsCollection, where('path', '==', path));
    const snapshot = await getDocs(pathQuery);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

        return {
      id: doc.id,
      name: data.name,
      type: data.type,
      url: data.url,
      path: data.path,
      uploadedAt: data.uploadedAt,
      status: data.status,
      userId: data.userId,
      size: data.size,
      checksum: data.checksum,
      analysisId: data.analysisId
    };
  } catch (error) {
    secureLog('❌ Firestore: Error getting document by path:', error);
    throw error;
  }
};

export const repairDocumentUrls = async (userId: string): Promise<{
  repaired: number;
  failed: number;
  total: number;
}> => {
  try {
    secureLog('🔧 Storage: Starting document URL repair:', { userId: '[REDACTED]' });

    const userDocs = await getUserDocuments(userId);
    let repaired = 0;
    let failed = 0;

    for (const doc of userDocs) {
      try {
        const newUrl = await refreshDocumentUrl(userId, doc.id);
        if (newUrl !== doc.url) {
          repaired++;
          secureLog('✅ Storage: URL repaired:', { documentId: doc.id });
        }
      } catch (error) {
        failed++;
        secureLog('❌ Storage: Failed to repair URL:', { documentId: doc.id, error });
      }
    }

    return {
      repaired,
      failed,
      total: userDocs.length
    };
  } catch (error) {
    console.error("Error repairing document URLs:", error);
    throw error;
  }
};